import { Box, styled } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { LwSplitButton } from 'redesign';
import { useState } from 'react';
import { useToggle } from 'shared/hooks';
import { DateTime } from 'luxon';
import { QUERY_KEYS } from 'api/query-keys';
import { useQueryClient } from 'react-query';
import { WeekDateControl } from 'shared/components/date-control-toolbox/week-date-control/week-date-control';
import { TableDateControl } from './components/table-date-control/table-date-control';
import { PlanningViewSwitcher } from './components/planning-view-switcher';
import { PlanningToolboxSearch } from './components/planning-toolbox-search/planning-toolbox-search';
import { useGetActions } from './hooks/use-get-actions';
import { PlanningFilterDataProvider } from './components/filters/planning-filter-data-provider';
import { ExportModal } from './components/export-drawer';
import {
  PlanningToolboxExportButton,
  PlanningToolboxFilterButton,
} from './components/planning-toolbox-utility-actions';
import { usePlanningParamsContext } from '../providers/planning-params-provider';

type CalendarQueryPaginatedData = { pages: unknown[]; pageParams: unknown[] } | undefined;

export const PlanningToolBox = () => {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const { on: filtersOpen, toggle: toggleFilters } = useToggle();
  const location = useLocation();
  const activeRoute = location.pathname.split('/').pop();
  const { actions, primaryButtonAction } = useGetActions(activeRoute);
  const { selectedFiltersCount } = usePlanningParamsContext();
  const queryClient = useQueryClient();

  const { filters, setFilters } = usePlanningParamsContext();

  const selectedDate = filters.date ? filters.date : DateTime.now();

  const trimShiftPlanningCache = () => {
    queryClient.setQueryData(
      QUERY_KEYS.shiftPlanning({
        company: filters.company,
        branchOffices: filters.branchOfficeIds,
        departments: filters.departmentIds,
        fromDate: selectedDate.startOf('week').toISODate(),
        toDate: selectedDate.endOf('week').toISODate(),
        jobType: filters.jobType,
        hasApplicants: filters.hasApplicants,
        filledStatus: filters.filledStatus,
        isFreelance: filters.isFreelance,
        isTempWork: filters.isTempWork,
        isEmployee: filters.isEmployee,
        search: filters.search,
      }),
      (old: CalendarQueryPaginatedData) => {
        if (!old) {
          return { pages: [], pageParams: [] };
        }
        return {
          ...old,
          pages: [old.pages[0]],
          pageParams: [old.pageParams[0]],
        };
      }
    );
  };

  const handleChangeDate = (newDate: DateTime) => {
    trimShiftPlanningCache();
    setFilters({ date: newDate });
  };

  return (
    <>
      <Toolbar>
        <ToolbarStart>
          <LwSplitButton
            onClick={primaryButtonAction.onClick}
            options={actions}
            primaryLabel={primaryButtonAction.label}
            disabledPrimary={primaryButtonAction.disabled}
            disabledDropdown={actions.length === 0}
            dataTestId="shift-action"
          />
        </ToolbarStart>
        <DateControlSlot>
          {activeRoute === 'table' ? (
            <TableDateControl />
          ) : (
            <WeekDateControl
              date={selectedDate}
              onChange={handleChangeDate}
              onToday={() => handleChangeDate(DateTime.now())}
            />
          )}
        </DateControlSlot>
        <UtilitySection>
          <SearchSlot>
            <PlanningToolboxSearch />
          </SearchSlot>
          <UtilityActionGroup>
            <PlanningViewSwitcher />
            <PlanningToolboxExportButton onClick={() => setExportModalOpen(true)} />
            <PlanningToolboxFilterButton
              onClick={toggleFilters}
              nrOfActiveFilters={selectedFiltersCount}
            />
          </UtilityActionGroup>
        </UtilitySection>
      </Toolbar>
      {filtersOpen ? <PlanningFilterDataProvider toggleFilters={toggleFilters} /> : null}
      {exportModalOpen ? (
        <ExportModal
          setExportModalOpen={() => setExportModalOpen(false)}
          exportModalOpen={exportModalOpen}
        />
      ) : null}
    </>
  );
};

const TOOLBAR_DATE_CONTROL_MIN_HEIGHT_PX = 100;

const Toolbar = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(4),
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  gridTemplateAreas: '"start date utilities"',
  marginTop: theme.spacing(8),
  overflow: 'hidden',
  minHeight: `${TOOLBAR_DATE_CONTROL_MIN_HEIGHT_PX}px`,
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    gridTemplateAreas: `
      "start date"
      "utilities utilities"
    `,
    gridTemplateColumns: 'minmax(min-content, 1fr) auto',
  },
}));

const ToolbarStart = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gridArea: 'start',
  justifySelf: 'start',
  minWidth: 'min-content',
});

const DateControlSlot = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gridArea: 'date',
  justifyContent: 'center',
  justifySelf: 'center',
  minHeight: `${TOOLBAR_DATE_CONTROL_MIN_HEIGHT_PX}px`,
  minWidth: 0,
});

const SearchSlot = styled(Box)({
  display: 'flex',
  minWidth: 0,
  width: '100%',
});

const UtilityActionGroup = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.spacing(2),
}));

const UtilitySection = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gap: theme.spacing(2),
  gridArea: 'utilities',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  justifySelf: 'stretch',
  minWidth: 0,
  width: '100%',
}));
